export const ticketCategories = [
  {
    value: "payment",
    label: "Billing & Payments",
    description: "Plan charges, failed payments, refunds, invoices, or billing history.",
  },
  {
    value: "access-modules",
    label: "Account & Module Access",
    description: "Login issues, locked content, missing modules, or dashboard access.",
  },
  {
    value: "study-support",
    label: "Study & Practice Help",
    description: "Practice guidance, mock-test help, study planning, or question support.",
  },
];

const STORAGE_KEY = "prepnexis_support_tickets";

const getStorage = () => {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    console.error("Error reading support tickets:", error);
    return [];
  }
};

const setStorage = (tickets) => {
  if (typeof window === "undefined") return;

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tickets));
};

const requestJson = async (url, options = {}) => {
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(data.message || data.error || "Request failed");
    error.status = response.status;
    throw error;
  }

  return data;
};

const shouldUseLocalFallback = (error) => !error.status || error.status === 404;

const toRoleList = (value) => {
  const roles = Array.isArray(value) ? value : [value].filter(Boolean);
  return roles.map((role) => Number(role)).filter((role) => Number.isFinite(role));
};

const getSupportRole = (roles) => roles.find((role) => role >= 1 && role <= 6) || 0;

const canUseSupport = (role, subscriptionStatus) => {
  if (role === 1) return true;
  return role >= 2 && role <= 6 && String(subscriptionStatus).toLowerCase() === "active";
};

const makeTicket = (payload) => {
  const now = new Date().toISOString();
  const id = `ticket-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  return {
    id,
    ticketNumber: `PNX-${Date.now().toString().slice(-6)}`,
    category: payload.category,
    subject: payload.subject,
    message: payload.message,
    status: "Open",
    priority: "Normal",
    userName: payload.userName || "Learner",
    userEmail: payload.userEmail || "Not available",
    userRole: payload.userRole || "",
    subscriptionStatus: payload.subscriptionStatus || "",
    unreadForUser: 0,
    unreadForAdmin: 1,
    rating: null,
    createdAt: now,
    updatedAt: now,
    messages: [
      {
        id: `${id}-message-1`,
        authorType: "user",
        authorName: payload.userName || "Learner",
        message: payload.message,
        createdAt: now,
      },
    ],
  };
};

export const getSupportAccess = async (dashboardData = {}) => {
  const [authResult, subscriptionResult, userResult] = await Promise.allSettled([
    requestJson("/api/check-auth"),
    requestJson("/api/current-subscription"),
    requestJson("/api/user-info"),
  ]);

  const auth = authResult.status === "fulfilled" ? authResult.value : {};
  const subscription = subscriptionResult.status === "fulfilled" ? subscriptionResult.value : {};
  const user = userResult.status === "fulfilled" ? userResult.value : {};
  const roles = toRoleList(auth.roles ?? subscription.userRole);
  const supportRole = getSupportRole(roles);
  const subscriptionStatus =
    subscription.subscriptionStatus ||
    subscription.currentSubscription?.subscriptionStatus ||
    dashboardData.subscriptionStatus ||
    dashboardData.currentSubscription?.subscriptionStatus ||
    "";

  return {
    eligible: canUseSupport(supportRole, subscriptionStatus),
    role: supportRole || roles[0] || 0,
    subscriptionStatus,
    user: {
      name: `${user.firstname || ""} ${user.lastname || ""}`.trim() || user.username || "Learner",
      email: user.username || user.email || "",
    },
  };
};

export const fetchSupportTickets = async ({ admin = false, userEmail = "" } = {}) => {
  const endpoint = admin ? "/api/admin/support-tickets" : "/api/support/tickets";

  try {
    const data = await requestJson(endpoint);
    const tickets = Array.isArray(data) ? data : data.tickets || [];
    return userEmail ? tickets.filter((ticket) => ticket.userEmail === userEmail) : tickets;
  } catch (error) {
    if (!shouldUseLocalFallback(error)) throw error;
    const tickets = getStorage();
    return userEmail ? tickets.filter((ticket) => ticket.userEmail === userEmail) : tickets;
  }
};

export const createSupportTicket = async (payload) => {
  try {
    const data = await requestJson("/api/support/tickets", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return data.ticket || data;
  } catch (error) {
    if (!shouldUseLocalFallback(error)) throw error;
    const ticket = makeTicket(payload);
    const tickets = [ticket, ...getStorage()];
    setStorage(tickets);
    return ticket;
  }
};

export const updateSupportTicket = async (ticketId, updates) => {
  try {
    const data = await requestJson(`/api/admin/support-tickets/${ticketId}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
    return data.ticket || data;
  } catch (error) {
    if (!shouldUseLocalFallback(error)) throw error;
    const now = new Date().toISOString();
    const tickets = getStorage();
    const nextTickets = tickets.map((ticket) => {
      if (ticket.id !== ticketId && ticket._id !== ticketId && ticket.ticketNumber !== ticketId) return ticket;

      const nextMessages = updates.reply
        ? [
            ...(ticket.messages || []),
            {
              id: `${ticketId}-reply-${Date.now()}`,
              authorType: "admin",
              authorName: "Admin",
              message: updates.reply,
              createdAt: now,
            },
          ]
        : ticket.messages || [];
      const unreadForUser = updates.reply ? (Number(ticket.unreadForUser) || 0) + 1 : ticket.unreadForUser || 0;

      return {
        ...ticket,
        status: updates.status || ticket.status,
        unreadForUser,
        updatedAt: now,
        messages: nextMessages,
      };
    });

    setStorage(nextTickets);
    return nextTickets.find((ticket) => ticket.id === ticketId || ticket._id === ticketId || ticket.ticketNumber === ticketId);
  }
};

export const replyToSupportTicket = async (ticketId, message) => {
  try {
    const data = await requestJson(`/api/support/tickets/${ticketId}/reply`, {
      method: "POST",
      body: JSON.stringify({ message }),
    });
    return data.ticket || data;
  } catch (error) {
    if (!shouldUseLocalFallback(error)) throw error;
    const now = new Date().toISOString();
    const tickets = getStorage();
    const nextTickets = tickets.map((ticket) => {
      if (ticket.id !== ticketId && ticket._id !== ticketId && ticket.ticketNumber !== ticketId) return ticket;
      if (ticket.status === "Resolved") {
        throw new Error("Resolved tickets are closed for replies");
      }

      return {
        ...ticket,
        unreadForAdmin: (Number(ticket.unreadForAdmin) || 0) + 1,
        updatedAt: now,
        messages: [
          ...(ticket.messages || []),
          {
            id: `${ticketId}-user-reply-${Date.now()}`,
            authorType: "user",
            authorName: ticket.userName || "Learner",
            message,
            createdAt: now,
          },
        ],
      };
    });

    setStorage(nextTickets);
    return nextTickets.find((ticket) => ticket.id === ticketId || ticket._id === ticketId || ticket.ticketNumber === ticketId);
  }
};

export const markSupportTicketRead = async (ticketId) => {
  try {
    const data = await requestJson(`/api/support/tickets/${ticketId}/read`, {
      method: "POST",
    });
    return data.ticket || data;
  } catch (error) {
    if (!shouldUseLocalFallback(error)) throw error;
    const tickets = getStorage();
    const nextTickets = tickets.map((ticket) =>
      ticket.id === ticketId || ticket._id === ticketId || ticket.ticketNumber === ticketId
        ? { ...ticket, unreadForUser: 0 }
        : ticket
    );

    setStorage(nextTickets);
    return nextTickets.find((ticket) => ticket.id === ticketId || ticket._id === ticketId || ticket.ticketNumber === ticketId);
  }
};

export const rateSupportTicket = async (ticketId, rating) => {
  try {
    const data = await requestJson(`/api/support/tickets/${ticketId}/rating`, {
      method: "POST",
      body: JSON.stringify(rating),
    });
    return data.ticket || data;
  } catch (error) {
    if (!shouldUseLocalFallback(error)) throw error;
    const now = new Date().toISOString();
    const score = Number(rating.score);

    if (!Number.isInteger(score) || score < 1 || score > 5) {
      throw new Error("Rating must be between 1 and 5");
    }

    const tickets = getStorage();
    const nextTickets = tickets.map((ticket) => {
      if (ticket.id !== ticketId && ticket._id !== ticketId && ticket.ticketNumber !== ticketId) return ticket;
      if (ticket.status !== "Resolved") {
        throw new Error("Only resolved tickets can be rated");
      }

      return {
        ...ticket,
        rating: {
          score,
          comment: rating.comment || "",
          ratedAt: now,
        },
      };
    });

    setStorage(nextTickets);
    return nextTickets.find((ticket) => ticket.id === ticketId || ticket._id === ticketId || ticket.ticketNumber === ticketId);
  }
};
